import React, {useEffect, useImperativeHandle, useMemo, useRef} from 'react';
import clsx from 'clsx';
import {Popover, type PopoverActions, type PopoverProps} from '@mui/material';
import {
	type ActionMenuListProps,
	renderActionMenuList,
} from '@netx/core-components/ActionMenuList';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {MenuList} from '@netx/core-components/MenuList';
import {
	composeClasses,
	styled,
	type WithClasses,
} from '@netx/core-components/Theme';
import {splitEventHandler} from '@netx/core-next/utils';
import {unwrapActionChildren} from '@netx/core-next/utils/actions';
import {
	ACTION_MENU_LIST_WRAPPER_CLASS_NAME,
	actionMenuListWrapperClasses,
	ActionMenuListWrapperClasses,
	getActionMenuListWrapperUtilityClass,
} from './actionMenuListWrapperClasses';
import {useActionSubMenu} from './hooks';

type ActionMenuListWrapperOwnerState =
	WithClasses<ActionMenuListWrapperClasses>;

const useUtilityClasses = (ownerState: ActionMenuListWrapperOwnerState) => {
	const {classes} = ownerState;
	const slots = {
		root: ['root'],
		popover: ['popover'],
		content: ['content'],
	};
	return composeClasses(slots, getActionMenuListWrapperUtilityClass, classes);
};

const ActionMenuListWrapperRoot = styled(MenuList, {
	name: ACTION_MENU_LIST_WRAPPER_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	overflowY: 'auto',
	overflowX: 'hidden',
}));

const ActionMenuListWrapperPopover = styled(Popover, {
	name: ACTION_MENU_LIST_WRAPPER_CLASS_NAME,
	slot: 'Popover',
})(({theme}) => ({
	'& .MuiList-root': {
		outline: 'none',
		minWidth: 175,
	},
	'& .MuiPaper-root': {
		backgroundColor: 'transparent',
		overflowY: 'visible',
		overflowX: 'visible',
		display: 'flex',
		minHeight: 0,
		flexDirection: 'column',
	},
}));

const ActionMenuListWrapperContent = styled('div', {
	name: ACTION_MENU_LIST_WRAPPER_CLASS_NAME,
	slot: 'Content',
})(({theme}) => ({
	marginLeft: theme.spacing(0.5),
	marginRight: theme.spacing(0.5),
	backgroundColor: theme.palette.background.paper,
	boxShadow: theme.shadows[7],
	overflowY: 'auto',
	overflowX: 'hidden',
	borderRadius: theme.shape.borderRadius,
	flex: '1 1 auto',
}));

export interface ActionMenuListWrapperProps
	extends
		ActionMenuListProps,
		React.HTMLAttributes<HTMLUListElement>,
		WithClasses<ActionMenuListWrapperClasses> {
	depth?: number;
	disableFlyout?: boolean;
	action?: React.RefObject<PopoverActions>;
	parentMenuOpen?: boolean;
	parentMenuRef: React.RefObject<HTMLElement>;
}

export const ActionMenuListWrapper = ({
	classes: _classes,
	className,
	action,
	actions,
	depth = 0,
	parentMenuOpen,
	parentMenuRef,
	disableIcons,
	disableFlyout,
	onAction,
	MenuItemProps,
	...props
}: ActionMenuListWrapperProps) => {
	const {
		anchorEl,
		activeAction,
		mainMenuOpen,
		setItemInteraction,
		startItemInteraction,
		stopItemInteraction,
		cancelStopMenuInteraction,
		startMenuInteraction,
		stopMenuInteraction,
	} = useActionSubMenu({
		depth,
	});

	const actionRef = React.useRef<PopoverActions>(null);
	const wrapperRef = React.useRef<HTMLUListElement>(null);

	const ownerState: ActionMenuListWrapperOwnerState = {
		classes: _classes,
		className,
	};

	const classes = useUtilityClasses(ownerState);

	const handleKeyBubblePrevention = (event: React.KeyboardEvent) => {
		switch (event.key) {
			// prevent these from bubbling up to the parent menu
			case 'ArrowDown':
			case 'ArrowUp':
				event.stopPropagation();
				break;
		}
	};

	const [anchorOrigin, setAnchorOrigin] = React.useState<
		Required<PopoverProps>['anchorOrigin']
	>({
		vertical: 'top',
		horizontal: 'right',
	});
	const [transformOrigin, setTransformOrigin] = React.useState<
		Required<PopoverProps>['anchorOrigin']
	>({
		vertical: 'top',
		horizontal: 'left',
	});

	const checkPosition = useConstCallback(() => {
		if (disableFlyout || !parentMenuRef.current || !parentMenuOpen || !anchorEl)
			return;
		const rect = parentMenuRef.current.getBoundingClientRect();
		if (rect.width === 0 || rect.height === 0) {
			wrapperRef.current?.style.setProperty('visibility', 'hidden');
			return;
		}
		parentMenuCheckedOnce.current = true;
		//if (rect.right + (rect.width * 0.6) >= window.innerWidth - 16) {
		if (rect.right + 200 >= window.innerWidth - 16) {
			setAnchorOrigin({
				vertical: 'top',
				horizontal: 'left',
			});
			setTransformOrigin({
				vertical: 'top',
				horizontal: 'right',
			});
		} else {
			setAnchorOrigin({
				vertical: 'top',
				horizontal: 'right',
			});
			setTransformOrigin({
				vertical: 'top',
				horizontal: 'left',
			});
		}
		actionRef.current?.updatePosition();
	});

	useImperativeHandle(
		action,
		() => ({
			updatePosition: checkPosition,
		}),
		[],
	);

	const parentMenuCheckedOnce = useRef(false);
	useEffect(() => {
		if (!parentMenuCheckedOnce.current) checkPosition();
	});

	useEffect(() => {
		window.addEventListener('resize', checkPosition);
		return () => {
			window.removeEventListener('resize', checkPosition);
		};
	}, []);

	const _actions = useMemo(() => {
		if (!disableFlyout) return actions;
		return unwrapActionChildren(actions);
	}, [actions, disableFlyout]);

	return (
		<ActionMenuListWrapperRoot
			{...props}
			className={clsx(classes.root, className)}
			data-depth={depth}
			ref={wrapperRef}
			onMouseEnter={splitEventHandler(props.onMouseEnter, () =>
				startMenuInteraction(),
			)}
			onMouseLeave={splitEventHandler(
				props.onMouseLeave,
				(event: React.MouseEvent<HTMLUListElement>) => {
					// prevent this from bubbling up to the parent menu
					event.stopPropagation();
					stopMenuInteraction();
				},
			)}
			onKeyDown={splitEventHandler(props.onKeyDown, handleKeyBubblePrevention)}
			onKeyUp={splitEventHandler(props.onKeyUp, handleKeyBubblePrevention)}
		>
			{renderActionMenuList({
				actions: _actions,
				disableIcons,
				onAction,
				MenuItemProps: (action) => {
					const _props =
						typeof MenuItemProps === 'function'
							? MenuItemProps(action)
							: MenuItemProps;
					return {
						..._props,
						active: anchorEl != null && activeAction?.name === action.name,
						// We cant use these otherwise going backwards with the keyboard is a nightmare
						// onBlur: splitEventHandler(_props?.onBlur, () => stopItemInteraction(action)),
						// onFocus: splitEventHandler(_props?.onFocus, (event) => startItemInteraction(action, event.currentTarget)),
						onKeyDown: splitEventHandler(_props?.onKeyDown, (event) => {
							switch (event.key) {
								case 'ArrowRight':
								case 'Enter':
								case ' ':
									if (action.children?.length) {
										setItemInteraction(action, event.currentTarget);
									}
									break;
							}
						}),
						onMouseEnter: splitEventHandler(_props?.onMouseEnter, (event) =>
							startItemInteraction(action, event.currentTarget),
						),
						onMouseLeave: splitEventHandler(_props?.onMouseLeave, () =>
							stopItemInteraction(action),
						),
					};
				},
			})}

			{parentMenuOpen && !disableFlyout && (
				<ActionMenuListWrapperPopover
					action={actionRef}
					anchorEl={anchorEl}
					anchorOrigin={anchorOrigin}
					className={classes.popover}
					transformOrigin={transformOrigin}
					TransitionProps={{
						onEntered: () => {
							checkPosition();
						},
					}}
					disablePortal
					hideBackdrop
					open={Boolean(anchorEl)}
					slotProps={{
						paper: {
							elevation: 0,
						},
					}}
				>
					<ActionMenuListWrapperContent className={classes.content}>
						<ActionMenuListWrapper
							actions={activeAction?.children ?? []}
							depth={depth + 1}
							disableIcons={disableIcons}
							parentMenuRef={wrapperRef}
							parentMenuOpen={Boolean(anchorEl)}
							onAction={onAction}
							{...props}
							onMouseEnter={splitEventHandler(props.onMouseEnter, () =>
								cancelStopMenuInteraction(),
							)}
						/>
					</ActionMenuListWrapperContent>
				</ActionMenuListWrapperPopover>
			)}
		</ActionMenuListWrapperRoot>
	);
};
